import { __decorate } from "tslib";
import { ArrayDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { Sorter } from '@stamhoofd/utility';
import { BalanceItemRelationType } from '../BalanceItem.js';
import { BalanceItemPaymentDetailed } from '../BalanceItemDetailed.js';
import { BaseOrganization } from '../Organization.js';
import { PaymentMethod, PaymentMethodHelper } from '../PaymentMethod.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
import { Payment, Settlement } from './Payment.js';
export class PaymentGeneral extends Payment {
    constructor() {
        super(...arguments);
        this.balanceItemPayments = [];
        this.iban = null;
        this.ibanName = null;
        /**
         * Only set for administrators with the correct permissions
         */
        this.settlement = null;
        /**
         * Only set for administrators with the correct permissions
         */
        this.transferFee = 0;
        this.serviceFeePayout = 0;
        this.serviceFeeManual = 0;
        this.serviceFeeManualCharged = 0;
        /**
         * Only set for administrators with the correct permissions
         */
        this.stripeAccountId = null;
        /**
         * Only set for administrators with the correct permissions
         */
        this.payingOrganization = null;
    }
    get calculatedPrice() {
        return this.balanceItemPayments.reduce((total, item) => total + item.price, 0);
    }
    get groupIds() {
        const ids = this.balanceItemPayments.flatMap((p) => {
            var _a;
            const id = (_a = p.balanceItem.relations.get(BalanceItemRelationType.Group)) === null || _a === void 0 ? void 0 : _a.id;
            return id ? [id] : [];
        });
        return Formatter.uniqueArray(ids);
    }
    get webshopIds() {
        const ids = this.balanceItemPayments.flatMap((p) => {
            var _a;
            const id = (_a = p.balanceItem.relations.get(BalanceItemRelationType.Webshop)) === null || _a === void 0 ? void 0 : _a.id;
            return id ? [id] : [];
        });
        return Formatter.uniqueArray(ids);
    }
    get memberNames() {
        const ids = this.balanceItemPayments.flatMap((p) => {
            var _a;
            const id = (_a = p.balanceItem.relations.get(BalanceItemRelationType.Member)) === null || _a === void 0 ? void 0 : _a.name.toString();
            return id ? [id] : [];
        });
        return Formatter.joinLast(Formatter.uniqueArray(ids), ', ', ' ' + $t(`%M1`) + ' ');
    }
    getBalanceItemPaymentsHtmlTable() {
        return getBalanceItemPaymentsHtmlTable(this.balanceItemPayments);
    }
    /**
     * Customer name, phone, payment method and total price
     */
    getPaymentDataHTMLTable() {
        var _a;
        let str = `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
        const customer = this.customer;
        const replacements = [];
        if (customer) {
            replacements.push({
                title: $t(`%Gq`),
                value: customer.dynamicName,
            });
            if (customer.phone) {
                replacements.push({
                    title: $t(`%18Z`),
                    value: customer.phone,
                });
            }
        }
        replacements.push({
            title: $t(`%M7`),
            value: Formatter.capitalizeFirstLetter(PaymentMethodHelper.getName((_a = this.method) !== null && _a !== void 0 ? _a : PaymentMethod.Unknown)),
        });
        replacements.push({
            title: $t(`%xL`),
            value: Formatter.price(this.price),
        });
        for (const replacement of replacements) {
            if (replacement.value.length === 0) {
                continue;
            }
            str += `<tr><td><h4>${Formatter.escapeHtml(replacement.title)}</h4></td><td>${Formatter.escapeHtml(replacement.value)}</td></tr>`;
        }
        return str + '</tbody></table>';
    }
    getShortDescription() {
        const shortDescriptions = this.balanceItemPayments.filter(p => p.price !== 0).map(p => p.balanceItem.paymentShortDescription).filter(p => p !== null);
        // Count the number of times each description occurs and add prefix if more than 1
        const counts = {};
        for (const shortDescription of shortDescriptions) {
            counts[shortDescription] = (counts[shortDescription] || 0) + 1;
        }
        // Add prefix if more than 1
        const arr = [];
        for (const shortDescription of Object.keys(counts)) {
            if (counts[shortDescription] > 1) {
                arr.push(counts[shortDescription] + ' x ' + shortDescription);
            }
            else {
                arr.push(shortDescription);
            }
        }
        return Formatter.capitalizeFirstLetter(Formatter.joinLast(arr, ', ', ' ' + $t(`%M1`) + ' '));
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(BalanceItemPaymentDetailed) })
], PaymentGeneral.prototype, "balanceItemPayments", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PaymentGeneral.prototype, "iban", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PaymentGeneral.prototype, "ibanName", void 0);
__decorate([
    field({ decoder: Settlement, nullable: true })
], PaymentGeneral.prototype, "settlement", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 196 }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], PaymentGeneral.prototype, "transferFee", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PaymentGeneral.prototype, "serviceFeePayout", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PaymentGeneral.prototype, "serviceFeeManual", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], PaymentGeneral.prototype, "serviceFeeManualCharged", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 198 })
], PaymentGeneral.prototype, "stripeAccountId", void 0);
__decorate([
    field({ decoder: BaseOrganization, nullable: true, version: 344 })
], PaymentGeneral.prototype, "payingOrganization", void 0);
export function getBalanceItemPaymentsHtmlTable(balanceItemPayments) {
    var _a;
    const payments = balanceItemPayments.slice().sort((a, b) => {
        var _a, _b;
        return Sorter.stack(Sorter.byNumberValue(a.price, b.price), Sorter.byStringValue((_a = a.itemDescription) !== null && _a !== void 0 ? _a : a.balanceItem.description, (_b = b.itemDescription) !== null && _b !== void 0 ? _b : b.balanceItem.description));
    });
    let str = '';
    str += `<table width="100%" cellspacing="0" cellpadding="0" class="email-data-table"><tbody>`;
    for (const payment of payments) {
        const title = payment.itemTitle;
        let description = Formatter.escapeHtml((_a = payment.itemDescription) !== null && _a !== void 0 ? _a : '');
        if (payment.quantity !== 1) {
            if (description) {
                description += `\n`;
            }
            description += `${Formatter.escapeHtml(Formatter.float(payment.quantity))} x ${Formatter.escapeHtml(Formatter.price(payment.unitPrice))}`;
        }
        const descriptionText = description ? `<p class="email-style-description-small pre-wrap">${description}</p>` : '';
        const titleColumn = `<td><h4 class="email-style-title-list">${Formatter.escapeHtml(title)}</h4>${descriptionText}</td>`;
        const price = payment.price === 0 ? $t('%1Mn') : Formatter.price(payment.price);
        const priceColumn = `<td>${Formatter.escapeHtml(price)}</td>`;
        str += `<tr>${titleColumn}${priceColumn}</tr>`;
    }
    return str + '</tbody></table>';
}
//# sourceMappingURL=PaymentGeneral.js.map